import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as TrashIcon } from "../assets/icons/trash.svg";
import { requestsService } from "../services/requestsService";

interface AddTextDocumentModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

const AddTextDocumentModal: React.FC<AddTextDocumentModalProps> = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [context, setContext] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
    // Reset the input value so the same file can be selected again if removed and re-added
    event.target.value = '';
  };

  const addFiles = (newFiles: File[]) => {
    if (newFiles.length === 0) return;
    
    // Filter for accepted file types
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/gif'
    ];
    
    const validFiles = newFiles.filter(file => {
      return allowedTypes.includes(file.type) || 
             file.name.toLowerCase().endsWith('.pdf') ||
             file.name.toLowerCase().endsWith('.doc') ||
             file.name.toLowerCase().endsWith('.docx') ||
             file.name.toLowerCase().endsWith('.txt');
    });

    // Filter out files that are already in the list
    setFiles(prev => {
      const existingFileNames = prev.map(f => f.name);
      const newUniqueFiles = validFiles.filter(file => 
        !existingFileNames.includes(file.name)
      );
      
      if (validFiles.length !== newFiles.length) {
        setError(`Some files were skipped. Only PDF, DOC, DOCX, TXT, and image files are allowed.`);
      } else if (newUniqueFiles.length !== validFiles.length) {
        setError(`Some files were skipped because they're already added.`);
      } else if (error) {
        setError(null);
      }
      
      return [...prev, ...newUniqueFiles];
    });
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    // Only set drag over to false if we're leaving the main container
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleSubmit = async () => {
    if (!context.trim() && files.length === 0) {
      setError("Please add either text content or upload files");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const createdRequest = await requestsService.createDocument({
        context: context.trim() || undefined,
        files: files.length > 0 ? files : undefined
      });
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Navigate to the created request after a short delay
      setTimeout(() => {
        onClose();
        navigate(`/requests/view/${createdRequest.request_id}`);
      }, 500);
    } catch (error: any) {
      console.error("Failed to create text/document request:", error);
      if (error.response?.status === 422 && error.response?.data?.errors) {
        setError(error.response.data.errors.join('. '));
      } else {
        setError(error instanceof Error ? error.message : "Failed to create request. Please try again.");
      }
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div 
      className="modal-backdrop" 
      onClick={handleBackdropClick}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
      }}
    >
      <div 
        className="modal-content"
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          maxWidth: "600px",
          width: "90%",
          maxHeight: "80vh",
          overflow: "auto",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        }}
      >
        {/* Header */}
        <div style={{
          padding: "24px 24px 20px 24px",
          borderBottom: "1px solid #e5e7eb",
          position: "relative"
        }}>
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "24px",
              right: "24px",
              background: "none",
              border: "none",
              color: "#6b7280",
              fontSize: "14px",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0
            }}
          >
            Cancel
          </button>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 600 }}>
            Add Text or Documents
          </h2>
          <p style={{ margin: "8px 0 0 0", fontSize: "14px", color: "#6b7280" }}>
            Share text content or upload documents you want us to incorporate
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: "24px" }}>
          {/* Text Content Section */}
          <div style={{ marginBottom: "24px" }}>
            <label 
              htmlFor="context"
              style={{ 
                display: "block",
                fontSize: "14px", 
                fontWeight: 500, 
                color: "#374151",
                marginBottom: "8px"
              }}
            >
              Text Content or Notes
            </label>
            <textarea
              id="context"
              value={context}
              onChange={(e) => {
                setContext(e.target.value);
                if (error) setError(null);
              }}
              placeholder="Add text content, notes, or instructions about what you want us to create..."
              style={{
                width: "90%",
                minHeight: "120px",
                padding: "12px",
                border: "1px solid #d1d5db",
                borderRadius: "6px",
                fontSize: "14px",
                resize: "vertical",
                outline: "none",
                transition: "border-color 0.15s",
                fontFamily: "inherit"
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#6366f1";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
              }}
            />
          </div>

          {/* File Upload Section */}
          <div 
            style={{ marginBottom: "24px" }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              marginBottom: "12px"
            }}>
              <label style={{ 
                fontSize: "14px", 
                fontWeight: 500, 
                color: "#374151" 
              }}>
                Upload Files (Optional)
              </label>
              <label className="tertiary-info-button" style={{ cursor: "pointer" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: "6px" }}>
                  <path d="M12 2L12 14M12 2L8 6M12 2L16 6M6 14L6 20C6 21.1046 6.89543 22 8 22L16 22C17.1046 22 18 21.1046 18 20L18 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Choose Files
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  style={{ display: "none" }}
                  accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
                />
              </label>
            </div>

            {/* File List */}
            {files.length > 0 && (
              <div style={{
                border: `1px solid ${isDragOver ? "#6366f1" : "#e5e7eb"}`,
                borderRadius: "6px",
                backgroundColor: isDragOver ? "#f0f9ff" : "#f9fafb",
                transition: "all 0.2s ease"
              }}>
                {files.map((file, index) => (
                  <div 
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 16px",
                      borderBottom: index < files.length - 1 ? "1px solid #e5e7eb" : "none"
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ 
                        fontSize: "14px", 
                        fontWeight: 500, 
                        color: "#374151",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}>
                        {file.name}
                      </div>
                      <div style={{ 
                        fontSize: "12px", 
                        color: "#6b7280",
                        marginTop: "2px"
                      }}>
                        {formatFileSize(file.size)}
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(index)}
                      style={{
                        padding: "6px",
                        backgroundColor: "transparent",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.15s",
                        marginLeft: "12px"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#fee2e2";
                        e.currentTarget.style.borderColor = "#fca5a5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.borderColor = "#e5e7eb";
                      }}
                    >
                      <TrashIcon style={{ width: "14px", height: "14px", color: "#ef4444" }} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {files.length === 0 && (
              <div 
                style={{
                  border: `2px dashed ${isDragOver ? "#6366f1" : "#d1d5db"}`,
                  borderRadius: "6px",
                  padding: "24px",
                  textAlign: "center",
                  color: isDragOver ? "#6366f1" : "#6b7280",
                  fontSize: "14px",
                  backgroundColor: isDragOver ? "#f0f9ff" : "transparent",
                  transition: "all 0.2s ease"
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: "0 auto 8px", display: "block", color: isDragOver ? "#6366f1" : "#9ca3af" }}>
                  <path d="M12 2L12 14M12 2L8 6M12 2L16 6M6 14L6 20C6 21.1046 6.89543 22 8 22L16 22C17.1046 22 18 21.1046 18 20L18 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {isDragOver 
                  ? "Drop files here to upload" 
                  : "Drag files here or click \"Choose Files\" to upload documents"
                }
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              marginBottom: "16px",
              padding: "12px",
              backgroundColor: "#fee2e2",
              border: "1px solid #fca5a5",
              borderRadius: "6px",
              color: "#dc2626",
              fontSize: "14px"
            }}>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "20px 24px",
          borderTop: "1px solid #e5e7eb",
          display: "flex",
          justifyContent: "flex-end"
        }}>
          <button
            onClick={handleSubmit}
            className="primary-button"
            disabled={isSubmitting}
            style={{
              opacity: isSubmitting ? 0.6 : 1,
              cursor: isSubmitting ? "not-allowed" : "pointer"
            }}
          >
            {isSubmitting ? "Creating..." : "Create Request"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddTextDocumentModal;